import React, {useState, useEffect, useCallback} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Tab,
    Tabs,
    Typography,
    CircularProgress,
    Snackbar,
    Alert,
    Divider,
} from '@mui/material';
import axiosInstance from "../AxiosInstance";
import Box from "@mui/material/Box";
import XeroIntegrationConnect from "./XeroIntegrationConnect";
import IntegrationProviderSelect from "./IntegrationProviderSelect";
import UnleashedIntegrationSettings from "./UnleashedIntegrationSettings";
import EposSettingsPage from "./EposSettingsPage";

const INTEGRATION_PROVIDER_UNLEASHED = 1;
const INTEGRATION_PROVIDER_XERO = 3;

function applyUnleashedIntegration(data, setters) {
    setters.setOrganisationIntegrationId(data.organisationIntegrationId || null);
    setters.setApiId(data.apiId || '');
    setters.setApiKey(data.apiKey || '');
    setters.setIntegrationEnabled(data.isEnabled || false);
    setters.setSelectedProductGroups(data.productGroups || []);
    setters.setAllProductsSelected(data.allProducts || false);
    setters.setSelectedSalesOrderGroups(data.salesOrderGroups || []);
    setters.setAllSalesOrderGroupsSelected(data.allSalesOrderGroups || false);
    setters.setIncludeNonSellable(data.includeNonSellable || false);
    setters.setAutoDisabledMessage(data.autoDisabledMessage || null);
}

function ThirdPartyIntegrationsPage({ enableThirdPartyIntegration = true, enableEpos = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [organisationIntegrationId, setOrganisationIntegrationId] = useState(null);
    const [providerId, setProviderId] = useState(INTEGRATION_PROVIDER_UNLEASHED);
    const [providerOptions, setProviderOptions] = useState([
        { id: INTEGRATION_PROVIDER_UNLEASHED, name: 'Unleashed' },
        { id: INTEGRATION_PROVIDER_XERO, name: 'Xero' }
    ]);
    const [lockedProviderId, setLockedProviderId] = useState(null);
    const [canAddXeroOverlay, setCanAddXeroOverlay] = useState(false);
    const [apiId, setApiId] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [integrationEnabled, setIntegrationEnabled] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionLabel, setConnectionLabel] = useState('');
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [loadingProductGroups, setLoadingProductGroups] = useState(false);
    const [loadingSalesOrderGroups, setLoadingSalesOrderGroups] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [productGroups, setProductGroups] = useState([]);
    const [selectedProductGroups, setSelectedProductGroups] = useState([]);
    const [allProductsSelected, setAllProductsSelected] = useState(false);
    const [salesOrderGroups, setSalesOrderGroups] = useState([]);
    const [selectedSalesOrderGroups, setSelectedSalesOrderGroups] = useState([]);
    const [allSalesOrderGroupsSelected, setAllSalesOrderGroupsSelected] = useState(false);
    const [includeNonSellable, setIncludeNonSellable] = useState(false);
    const [autoDisabledMessage, setAutoDisabledMessage] = useState(null);
    const [xeroConnected, setXeroConnected] = useState(false);
    const [xeroEnabled, setXeroEnabled] = useState(false);
    const [xeroConnectionLabel, setXeroConnectionLabel] = useState('');
    const [xeroAutoDisabledMessage, setXeroAutoDisabledMessage] = useState(null);
    const [xeroSelectedCustomerIds, setXeroSelectedCustomerIds] = useState([]);
    const [xeroOverlayCustomers, setXeroOverlayCustomers] = useState([]);
    const [loadingOverlayCustomers, setLoadingOverlayCustomers] = useState(false);

    const fetchUnleashedIntegration = useCallback(async () => {
        const response = await axiosInstance.get('/settings', {
            params: { providerId: INTEGRATION_PROVIDER_UNLEASHED }
        });
        applyUnleashedIntegration(response.data, {
            setOrganisationIntegrationId,
            setApiId,
            setApiKey,
            setIntegrationEnabled,
            setSelectedProductGroups,
            setAllProductsSelected,
            setSelectedSalesOrderGroups,
            setAllSalesOrderGroupsSelected,
            setIncludeNonSellable,
            setAutoDisabledMessage
        });
    }, []);

    const fetchXeroIntegration = useCallback(async () => {
        const response = await axiosInstance.get('/settings', {
            params: { providerId: INTEGRATION_PROVIDER_XERO }
        });
        const data = response.data;
        setXeroConnected(data.isConnected || false);
        setXeroEnabled(data.isEnabled || false);
        setXeroConnectionLabel(data.connectionLabel || '');
        setXeroAutoDisabledMessage(data.autoDisabledMessage || null);
        setXeroSelectedCustomerIds(data.selectedCustomerIds || []);
        setIsConnected(data.isConnected || false);
        setConnectionLabel(data.connectionLabel || '');
        return data;
    }, []);

    const fetchOverlayCustomers = useCallback(async () => {
        setLoadingOverlayCustomers(true);
        try {
            const response = await axiosInstance.get('/xero/overlay-customers');
            setXeroOverlayCustomers(response.data || []);
            await fetchXeroIntegration();
        } catch (err) {
            setError(
                err.response?.data?.message ||
                err.response?.data ||
                'Failed to load Xero customers.'
            );
            setXeroOverlayCustomers([]);
        } finally {
            setLoadingOverlayCustomers(false);
        }
    }, [fetchXeroIntegration]);

    const fetchIntegration = useCallback(async (selectedProviderId) => {
        if (selectedProviderId === INTEGRATION_PROVIDER_UNLEASHED) {
            await fetchUnleashedIntegration();
            return;
        }

        const data = await fetchXeroIntegration();
        setOrganisationIntegrationId(data.organisationIntegrationId || null);
        setIntegrationEnabled(data.isEnabled || false);
        setAutoDisabledMessage(data.autoDisabledMessage || null);
    }, [fetchUnleashedIntegration, fetchXeroIntegration]);

    const initializePage = useCallback(async () => {
        setLoading(true);

        if (!enableThirdPartyIntegration) {
            setLoading(false);
            return;
        }

        try {
            const params = new URLSearchParams(window.location.search);
            const providerParam = params.get('provider');
            const status = params.get('status');
            const message = params.get('message');

            const providersResponse = await axiosInstance.get('/settings/providers');
            const providers = providersResponse.data.providers || [];
            setProviderOptions(providers);
            setLockedProviderId(providersResponse.data.lockedProviderId || null);
            const overlayAvailable = !!providersResponse.data.canAddXeroOverlay;
            setCanAddXeroOverlay(overlayAvailable);

            const requestedProviderId = overlayAvailable
                ? INTEGRATION_PROVIDER_UNLEASHED
                : (providersResponse.data.lockedProviderId
                    || (providerParam === 'xero'
                        ? INTEGRATION_PROVIDER_XERO
                        : providersResponse.data.selectedProviderId || INTEGRATION_PROVIDER_UNLEASHED));

            setProviderId(requestedProviderId);
            await fetchIntegration(requestedProviderId);

            if (overlayAvailable) {
                const xeroData = await fetchXeroIntegration();
                if (xeroData.isConnected) {
                    await fetchOverlayCustomers();
                }
            }

            if (status === 'connected')
                setSuccessMessage('Xero connected successfully.');
            else if (status === 'error')
                setError(message || 'Failed to connect to Xero.');

            if (status || providerParam || message) {
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to fetch organisation integration. Please try again later.'
            );
        } finally {
            setLoading(false);
        }
    }, [enableThirdPartyIntegration, fetchIntegration, fetchOverlayCustomers, fetchXeroIntegration]);

    useEffect(() => {
        initializePage();
    }, [initializePage]);

    const persistIntegration = async (overrides = {}) => {
        setSavingSettings(true);
        try {
            const payload = {
                organisationIntegrationId,
                integrationProviderId: providerId,
                apiId: providerId === INTEGRATION_PROVIDER_UNLEASHED ? (apiId || null) : null,
                apiKey: providerId === INTEGRATION_PROVIDER_UNLEASHED ? (apiKey || null) : null,
                isEnabled: integrationEnabled,
                productGroups: selectedProductGroups,
                allProductsSelected,
                salesOrderGroups: selectedSalesOrderGroups,
                allSalesOrderGroupsSelected,
                includeNonSellable,
                ...overrides
            };

            await axiosInstance.put('/settings', payload);
            await fetchIntegration(providerId);
            setSuccessMessage('Changes saved successfully!');

            if ((overrides.isEnabled ?? integrationEnabled) === true) {
                setAutoDisabledMessage(null);
            }

            if (window.refreshNotifications)
                window.refreshNotifications();
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to save organisation integration. Please try again later.'
            );
        } finally {
            setSavingSettings(false);
        }
    };

    const persistXeroSettings = async ({ isEnabled, selectedCustomerIds, successMessage: savedMessage }) => {
        setSavingSettings(true);
        try {
            await axiosInstance.put('/settings', {
                integrationProviderId: INTEGRATION_PROVIDER_XERO,
                isEnabled,
                selectedCustomerIds
            });
            await fetchXeroIntegration();
            setSuccessMessage(savedMessage);
            if (window.refreshNotifications)
                window.refreshNotifications();
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to save Xero settings. Please try again later.'
            );
        } finally {
            setSavingSettings(false);
        }
    };

    const persistXeroOverlay = async () => {
        const hasCustomers = xeroSelectedCustomerIds.length > 0;
        await persistXeroSettings({
            isEnabled: hasCustomers,
            selectedCustomerIds: xeroSelectedCustomerIds,
            successMessage: 'Xero customers saved successfully!'
        });
    };

    const handleXeroToggle = async (checked) => {
        if (checked && xeroSelectedCustomerIds.length === 0)
            return;

        setXeroEnabled(checked);
        await persistXeroSettings({
            isEnabled: checked,
            selectedCustomerIds: xeroSelectedCustomerIds,
            successMessage: 'Changes saved successfully!'
        });
    };

    const handleToggle = async (event) => {
        const isEnabled = event.target.checked;
        setIntegrationEnabled(isEnabled);

        if (!isEnabled && activeStep !== 0)
            setActiveStep(0);

        await persistIntegration({ isEnabled });
    };

    const fetchProductGroups = async (id, key) => {
        setLoadingProductGroups(true);
        try {
            const response = await axiosInstance.post('/settings/unleashedproductgroups', {
                apiId: id || apiId,
                apiKey: key || apiKey
            });
            setProductGroups(response.data || []);
            return true;
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to fetch product groups from Unleashed. Please check your API credentials.'
            );
            return false;
        } finally {
            setLoadingProductGroups(false);
        }
    };

    const fetchSalesOrderGroups = async (id, key) => {
        setLoadingSalesOrderGroups(true);
        try {
            const response = await axiosInstance.post('/settings/unleashedsalesordergroups', {
                apiId: id || apiId,
                apiKey: key || apiKey
            });
            const groups = response.data || [];
            setSalesOrderGroups(groups);
            if (groups.length === 0) {
                setAllSalesOrderGroupsSelected(false);
                setSelectedSalesOrderGroups([]);
            }
            return true;
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to fetch sales order groups from Unleashed. Please check your API credentials.'
            );
            return false;
        } finally {
            setLoadingSalesOrderGroups(false);
        }
    };

    const handleNext = async () => {
        if (providerId !== INTEGRATION_PROVIDER_UNLEASHED)
            return;

        if (activeStep === 0) {
            if (!apiId || !apiKey) {
                setError('Please enter both API ID and API Key.');
                return;
            }

            const [productGroupsSuccess, salesOrderGroupsSuccess] = await Promise.all([
                fetchProductGroups(),
                fetchSalesOrderGroups()
            ]);
            const success = productGroupsSuccess && salesOrderGroupsSuccess;
            if (success) {
                setActiveStep(1);
            }
        } else {
            await handleSave();
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleAllProductsChange = (event) => {
        const checked = event.target.checked;
        setAllProductsSelected(checked);
        if (checked) {
            setSelectedProductGroups([]);
        }
    };

    const handleSave = async () => {
        await persistIntegration();
        setActiveStep(0);
    };

    const handleProviderChange = async (nextProviderId) => {
        setProviderId(nextProviderId);
        setActiveStep(0);
        setLoading(true);

        try {
            await fetchIntegration(nextProviderId);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to load organisation integration.'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleXeroConnect = async () => {
        setSavingSettings(true);
        try {
            const response = await axiosInstance.post('/xero/connect', {
                frontendOrigin: window.location.origin
            });
            window.location.assign(response.data.authorizationUrl);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                'Failed to start Xero connection. Please try again later.'
            );
            setSavingSettings(false);
        }
    };

    const steps = ['API Credentials', 'Sync Configuration'];
    const hasProductSelection = allProductsSelected || selectedProductGroups.length > 0;
    const canEnableUnleashed = apiId && apiKey && hasProductSelection;
    const showUnleashed = providerId === INTEGRATION_PROVIDER_UNLEASHED || canAddXeroOverlay;
    const showXeroOnly = providerId === INTEGRATION_PROVIDER_XERO && !canAddXeroOverlay;
    const eposTabSelected = enableEpos && location.pathname === '/settings/integrations/epos';
    const selectedTab = eposTabSelected || !enableThirdPartyIntegration ? 'epos' : 'business';

    const handleTabChange = (_event, value) => {
        navigate(value === 'epos' ? '/settings/integrations/epos' : '/settings/integrations');
    };

    return (
        <Box className="component-container">
            <Container maxWidth="md" sx={{ mb: 3 }}>
                <Tabs value={selectedTab} onChange={handleTabChange} aria-label="Integration settings">
                    {enableThirdPartyIntegration && <Tab label="Business integrations" value="business" />}
                    {enableEpos && <Tab label="EPOS" value="epos" />}
                </Tabs>
            </Container>

            {selectedTab === 'business' && enableThirdPartyIntegration && <Container component="main" maxWidth={canAddXeroOverlay ? 'md' : 'sm'}>
                <Paper
                    elevation={3}
                    style={{
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    {loading ? (
                        <CircularProgress style={{marginTop: '20px'}}/>
                    ) : (
                        <>
                            {!lockedProviderId && (
                                <IntegrationProviderSelect
                                    providerId={providerId}
                                    providerOptions={providerOptions}
                                    onChange={handleProviderChange}
                                />
                            )}

                            {showUnleashed && (
                                <UnleashedIntegrationSettings
                                    activeStep={activeStep}
                                    steps={steps}
                                    apiId={apiId}
                                    apiKey={apiKey}
                                    integrationEnabled={integrationEnabled}
                                    canEnableIntegration={canEnableUnleashed}
                                    savingSettings={savingSettings}
                                    loadingProductGroups={loadingProductGroups}
                                    loadingSalesOrderGroups={loadingSalesOrderGroups}
                                    autoDisabledMessage={autoDisabledMessage}
                                    productGroups={productGroups}
                                    selectedProductGroups={selectedProductGroups}
                                    allProductsSelected={allProductsSelected}
                                    includeNonSellable={includeNonSellable}
                                    salesOrderGroups={salesOrderGroups}
                                    selectedSalesOrderGroups={selectedSalesOrderGroups}
                                    allSalesOrderGroupsSelected={allSalesOrderGroupsSelected}
                                    onApiIdChange={setApiId}
                                    onApiKeyChange={setApiKey}
                                    onToggleEnabled={(checked) => handleToggle({ target: { checked } })}
                                    onAllProductsChange={(checked) => handleAllProductsChange({ target: { checked } })}
                                    onProductGroupsChange={(value) => setSelectedProductGroups(value)}
                                    onIncludeNonSellableChange={setIncludeNonSellable}
                                    onAllSalesOrderGroupsChange={(checked) => {
                                        setAllSalesOrderGroupsSelected(checked);
                                        if (checked) setSelectedSalesOrderGroups([]);
                                    }}
                                    onSalesOrderGroupsChange={(value) => setSelectedSalesOrderGroups(value)}
                                    onBack={handleBack}
                                    onNext={handleNext}
                                />
                            )}

                            {canAddXeroOverlay && (
                                <Box sx={{ width: '100%', mt: 4 }}>
                                    <Divider sx={{ mb: 3 }} />
                                    <XeroIntegrationConnect
                                        overlayMode
                                        isConnected={xeroConnected}
                                        isEnabled={xeroEnabled}
                                        isSaving={savingSettings}
                                        connectionLabel={xeroConnectionLabel}
                                        autoDisabledMessage={xeroAutoDisabledMessage}
                                        overlayCustomers={xeroOverlayCustomers}
                                        selectedCustomerIds={xeroSelectedCustomerIds}
                                        loadingOverlayCustomers={loadingOverlayCustomers}
                                        onConnect={handleXeroConnect}
                                        onToggleEnabled={handleXeroToggle}
                                        onSelectedCustomersChange={setXeroSelectedCustomerIds}
                                        onSaveOverlaySelection={persistXeroOverlay}
                                    />
                                </Box>
                            )}

                            {showXeroOnly && (
                                <XeroIntegrationConnect
                                    isConnected={isConnected}
                                    isEnabled={integrationEnabled}
                                    isSaving={savingSettings}
                                    connectionLabel={connectionLabel}
                                    autoDisabledMessage={autoDisabledMessage}
                                    onConnect={handleXeroConnect}
                                    onToggleEnabled={(checked) => handleToggle({ target: { checked } })}
                                />
                            )}
                        </>
                    )}
                </Paper>

                <Snackbar
                    open={!!error}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    autoHideDuration={6000}
                    onClose={() => setError('')}
                >
                    <Alert onClose={() => setError('')} severity="error" sx={{width: '100%'}}>
                        {error}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={!!successMessage}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    autoHideDuration={6000}
                    onClose={() => setSuccessMessage('')}
                >
                    <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{width: '100%'}}>
                        {successMessage}
                    </Alert>
                </Snackbar>
            </Container>}

            {selectedTab === 'epos' && enableEpos && (
                <Container component="section" maxWidth="md" sx={{ mt: 3 }}>
                    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                        <EposSettingsPage embedded />
                    </Paper>
                </Container>
            )}
        </Box>
    );
}

export default ThirdPartyIntegrationsPage;
